"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useTransition } from "react";
import { useParams } from "next/navigation";
import posthog from "posthog-js";
import { Eyebrow } from "./eyebrow.component";
import { ConciergeBoard } from "./concierge-board";
import { CinematicWizard } from "./concierge-wizard-cinematic";
import { generatePlan } from "@/lib/actions/concierge";
import { buildConciergeFormData } from "@/lib/concierge/wizard-answer-map";
import type { DayPlan } from "@/lib/concierge/types";

/* -------------------------------------------------------------------------- */
/* Data                                                                       */
/* -------------------------------------------------------------------------- */

type DurationId = "1h" | "2-3h" | "half" | "full";
type GroupId = "solo" | "couple" | "family" | "friends";
type PaceId = "easy" | "balanced" | "packed";
type InterestId = "art" | "food" | "shop" | "nature" | "history" | "family" | "sundowner";
type BudgetId = "light" | "mid" | "spoil";

/* -------------------------------------------------------------------------- */
/* Step configuration                                                         */
/* -------------------------------------------------------------------------- */

const STEPS = [{
  id: "date",
  short: "Your date",
  title: "When are you visiting?",
  helper: "We'll tailor every stop to the day."
}, {
  id: "arrivalTime",
  short: "Arrival",
  title: "What time will you arrive?",
  helper: "We'll only suggest places that are open when you get there."
}, {
  id: "duration",
  short: "Your time",
  title: "How long do you have?",
  helper: "We'll size your day to fit."
}, {
  id: "group",
  short: "Who's with you",
  title: "Who's coming along?",
  helper: "So we set the right pace."
}, {
  id: "pace",
  short: "Your pace",
  title: "What's your kind of day?",
  helper: "Linger, balanced, or pack it in?"
}, {
  id: "interests",
  short: "What you love",
  title: "What pulls you in?",
  helper: "Pick a few — we'll thread them together."
}, {
  id: "budget",
  short: "Your budget",
  title: "And the budget?",
  helper: "We'll make every Rand count."
}] as const;

// Quick date presets for the date step (local ISO YYYY-MM-DD).
function quickDatePicks(): {
  label: string;
  iso: string;
}[] {
  const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const weekend = new Date(today);
  weekend.setDate(today.getDate() + (6 - today.getDay() + 7) % 7);
  return [{
    label: "Today",
    iso: iso(today)
  }, {
    label: "Tomorrow",
    iso: iso(tomorrow)
  }, {
    label: "This weekend",
    iso: iso(weekend)
  }];
}
type Answers = {
  date: string | null;
  arrivalTime: string | null;
  duration: DurationId | null;
  group: GroupId | null;
  pace: PaceId | null;
  interests: InterestId[];
  budget: BudgetId | null;
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export function VariantConcierge(t0) {
  const $ = _c(46);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    standalone: t2,
    initialPlan: t3,
    initialPlanId: t4
  } = t1;
  const standalone = t2 === undefined ? false : t2;
  const initialPlan = t3 === undefined ? null : t3;
  const initialPlanId = t4 === undefined ? null : t4;
  const params = useParams();
  const locale = typeof params?.locale === "string" && params.locale ? params.locale : "en";
  const [pending, startTransition] = useTransition();
  const [submitError, setSubmitError] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(_temp);
  const [showResult, setShowResult] = useState(!!initialPlan);
  const [realPlan, setRealPlan] = useState(initialPlan);
  const [planId, setPlanId] = useState(initialPlanId);
  const sectionRef = useRef(null);
  const hasMountedStep = useRef(false);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (!hasMountedStep.current) {
        hasMountedStep.current = true;
        return;
      }
      sectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== step) {
    t6 = [step];
    $[3] = step;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  useEffect(t5, t6);
  const resultRef = useRef(null);
  const hadResult = useRef(!!initialPlan);
  let t7;
  let t8;
  if ($[5] !== realPlan || $[6] !== showResult) {
    t7 = () => {
      if (!showResult || !realPlan) {
        return;
      }
      if (hadResult.current) {
        return;
      }
      hadResult.current = true;
      sectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      resultRef.current?.focus({
        preventScroll: true
      });
    };
    t8 = [showResult, realPlan];
    $[5] = realPlan;
    $[6] = showResult;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t9;
  if ($[9] !== answers || $[10] !== locale || $[11] !== step) {
    t9 = function next() {
      if (step < STEPS.length - 1) {
        setStep(step + 1);
        return;
      }
      setSubmitError(null);
      const fd = buildConciergeFormData(answers);
      posthog.capture("plan_started", {
        arrival_time: answers.arrivalTime,
        duration: answers.duration,
        group: answers.group,
        pace: answers.pace,
        interests: answers.interests,
        budget: answers.budget
      });
      startTransition(async () => {
        const res = await generatePlan(null, fd);
        if (!res.ok) {
          setSubmitError(res.error);
          posthog.captureException(new Error(`Plan generation failed: ${res.error}`));
          return;
        }
        posthog.capture("plan_generated", {
          plan_id: res.planId,
          arrival_time: answers.arrivalTime,
          duration: answers.duration,
          group: answers.group,
          pace: answers.pace,
          interests: answers.interests,
          budget: answers.budget,
          stop_count: res.plan.items.filter(_temp2).length
        });
        setRealPlan(res.plan);
        setPlanId(res.planId);
        setShowResult(true);
        if (typeof window !== "undefined") {
          window.history.replaceState(null, "", `/${locale}/plan-your-visit/${res.planId}`);
        }
      });
    };
    $[9] = answers;
    $[10] = locale;
    $[11] = step;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const next = t9;
  let t10;
  if ($[13] !== showResult || $[14] !== step) {
    t10 = function back() {
      if (showResult) {
        setShowResult(false);
      } else {
        if (step > 0) {
          setStep(step - 1);
        }
      }
    };
    $[13] = showResult;
    $[14] = step;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const back = t10;
  let t11;
  if ($[16] !== locale) {
    t11 = function reset() {
      setAnswers({
        date: quickDatePicks()[0]?.iso ?? "",
        arrivalTime: null,
        duration: null,
        group: null,
        pace: null,
        interests: [],
        budget: null
      });
      setStep(0);
      setShowResult(false);
      setRealPlan(null);
      setPlanId(null);
      hadResult.current = false;
      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", `/${locale}/plan-your-visit`);
      }
    };
    $[16] = locale;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const reset = t11;
  const t12 = standalone ? "-mt-2 tablet:-mt-8 desktop-sm:-mt-6" : "bg-white pt-20 pb-24 sm:pt-28 sm:pb-32";
  const t13 = pending ? "Building your day, please wait." : "";
  const t14 = showResult && realPlan ? "Your plan is ready." : "";
  let t15;
  if ($[18] !== t13 || $[19] !== t14) {
    t15 = <p aria-live="polite" aria-atomic="true" className="sr-only">{t13}{t14}</p>;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = standalone ? "w-full" : "mx-auto max-w-7xl px-5 sm:px-8";
  let t17;
  if ($[21] !== standalone) {
    t17 = !standalone && <div className="mb-10 flex flex-col gap-4 sm:mb-14 sm:flex-row sm:items-end sm:justify-between sm:gap-8"><div className="max-w-2xl"><Eyebrow className="mb-3">Variant 02b · Live concierge desk</Eyebrow><h2 className="typography-mobile-heading-h1-voyage desktop:typography-desktop-heading-h1-voyage text-deep-sea-blue-500 text-balance">Tell us about you. <br className="hidden sm:block" />We'll build the day.</h2></div><p className="max-w-sm typography-body-md-regular text-deep-sea-blue-500/65">Five quick questions and our concierge crafts an itinerary tuned to your time, taste and pace — with times, walking, and a stylised harbour map.</p></div>;
    $[21] = standalone;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const t18 = standalone ? "overflow-hidden" : "overflow-hidden rounded-2xl border border-deep-sea-blue-500/10 bg-white shadow-[0_30px_80px_-40px_rgba(31,42,58,0.25)]";
  let t19;
  if ($[23] !== answers || $[24] !== back || $[25] !== locale || $[26] !== next || $[27] !== pending || $[28] !== planId || $[29] !== realPlan || $[30] !== reset || $[31] !== showResult || $[32] !== step || $[33] !== submitError) {
    t19 = !showResult || !realPlan ? <CinematicWizard layout="mosaic" step={step} answers={answers} setAnswers={setAnswers} onNext={next} onBack={back} pending={pending} submitError={submitError} /> : <div ref={resultRef} tabIndex={-1} className="focus:outline-none"><ConciergeBoard plan={realPlan} planId={planId} locale={locale} variant="timeline" onReplan={reset} /></div>;
    $[23] = answers;
    $[24] = back;
    $[25] = locale;
    $[26] = next;
    $[27] = pending;
    $[28] = planId;
    $[29] = realPlan;
    $[30] = reset;
    $[31] = showResult;
    $[32] = step;
    $[33] = submitError;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t18 || $[36] !== t19) {
    t20 = <div className={t18}>{t19}</div>;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t16 || $[39] !== t17 || $[40] !== t20) {
    t21 = <div className={t16}>{t17}{t20}</div>;
    $[38] = t16;
    $[39] = t17;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t12 || $[43] !== t15 || $[44] !== t21) {
    t22 = <section ref={sectionRef} id="variant-concierge" aria-label="Plan your visit" data-planner-surface={true} className={t12}>{t15}{t21}</section>;
    $[42] = t12;
    $[43] = t15;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  return t22;
}
function _temp2(i) {
  return i.kind === "stop";
}
function _temp() {
  return {
    date: quickDatePicks()[0]?.iso ?? "",
    arrivalTime: null,
    duration: null,
    group: null,
    pace: null,
    interests: [],
    budget: null
  };
}